/**
 * BookThatApp hire widgets add via /cart/add.js, then redirect() to /cart.
 * After a hire line is removed they also reload the page while cleaning up
 * the security bond / addons. Keep those updates inside the cart drawer.
 */
(function () {
  if (window.__btaCartDrawerHooked) return;
  window.__btaCartDrawerHooked = true;

  const BTA_ABORT = 'bta-theme-cart-drawer';
  const INTENT_KEY = 'btaDrawerIntent';
  const OPEN_KEY = 'btaOpenDrawer';
  let suppressCartRedirect = false;
  let suppressReload = false;
  let suppressTimer;
  let reloadTimer;
  let refreshTimer;

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartJsUrl() {
    return `${shopRoot().replace(/\/$/, '')}/cart.js`;
  }

  function isCartPath(pathname) {
    return pathname === '/cart' || pathname === '/cart/' || /(^|\/)cart\/?$/.test(pathname);
  }

  function isCartUrl(value) {
    try {
      const url = new URL(String(value), window.location.origin);
      return isCartPath(url.pathname);
    } catch (error) {
      return /\/cart\/?(\?|$)/.test(String(value));
    }
  }

  function isSamePage(value) {
    try {
      const url = new URL(String(value), window.location.origin);
      return url.origin === window.location.origin && url.pathname === window.location.pathname;
    } catch (error) {
      return false;
    }
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
  }

  function currentPathForBta() {
    const root = shopRoot();
    let path = window.location.pathname || '/';
    if (root !== '/' && path.indexOf(root.replace(/\/$/, '')) === 0) {
      path = path.slice(root.replace(/\/$/, '').length);
    }
    if (path.charAt(0) === '/') path = path.slice(1);
    return path + (window.location.search || '') + (window.location.hash || '');
  }

  function armSuppress() {
    suppressCartRedirect = true;
    try {
      sessionStorage.setItem(INTENT_KEY, String(Date.now()));
    } catch (error) {
      // Ignore sessionStorage failures (private mode).
    }
    clearTimeout(suppressTimer);
    suppressTimer = setTimeout(() => {
      suppressCartRedirect = false;
    }, 8000);
  }

  function armReloadSuppress() {
    suppressReload = true;
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      suppressReload = false;
    }, 5000);
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function cartSectionsUrl() {
    return `${window.location.pathname}${window.location.search}${
      window.location.search ? '&' : '?'
    }sections=cart-drawer,cart-icon-bubble`;
  }

  function applyEmptyState(drawer, itemCount) {
    if (!drawer || typeof itemCount !== 'number') return;
    drawer.classList.toggle('is-empty', itemCount === 0);
    drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', itemCount === 0);
    drawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', itemCount === 0);
  }

  function requestMethod(input, init) {
    if (init && init.method) return String(init.method).toUpperCase();
    if (input && input.method) return String(input.method).toUpperCase();
    return 'GET';
  }

  function isPostCartMutation(url, method) {
    return isCartMutationUrl(url) && String(method || 'GET').toUpperCase() !== 'GET';
  }

  function refreshCartDrawer(options = {}) {
    const drawer = getCartDrawer();
    if (!drawer) return Promise.resolve();

    const shouldOpen = options.open === true || drawer.classList.contains('active');

    return Promise.all([
      fetch(cartSectionsUrl()).then((response) => response.json()),
      fetch(cartJsUrl())
        .then((response) => response.json())
        .catch(() => null),
    ])
      .then(([sections, cart]) => {
        if (typeof drawer.renderContents === 'function') {
          drawer.renderContents({ sections: sections || {} }, { open: shouldOpen });
        } else if (shouldOpen && typeof drawer.open === 'function') {
          drawer.open();
        }
        applyEmptyState(drawer, cart && cart.item_count);
      })
      .catch(() => {
        if (shouldOpen && typeof drawer.open === 'function') drawer.open();
      });
  }

  function openThemeCartDrawer() {
    refreshCartDrawer({ open: true });
  }

  function onCartMutated() {
    const drawer = getCartDrawer();
    if (!drawer || !drawer.classList.contains('active')) return;
    armReloadSuppress();
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refreshCartDrawer({ open: false }), 350);
  }

  function resetBtaWidget() {
    window.queueLoading = false;

    const buttons = document.querySelectorAll(
      'product-form [type="submit"], form[action*="/cart/add"] [type="submit"], #bta-product-widget button, [id^="bta-"] button, [class*="bta-"] button'
    );
    buttons.forEach((button) => {
      button.disabled = false;
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading', 'bta-loading', 'bta-added', 'bta-error');
    });

    document.querySelectorAll('product-form .loading-overlay__spinner').forEach((spinner) => {
      spinner.classList.add('hidden');
    });
    document.querySelectorAll('product-form').forEach((form) => {
      form.classList.remove('loading');
    });
  }

  function scheduleDrawerOpen() {
    armSuppress();
    armReloadSuppress();
    resetBtaWidget();
    [0, 150, 500].forEach((delay) => setTimeout(resetBtaWidget, delay));
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(openThemeCartDrawer, 250);
  }

  function itemsAddedToCart() {
    scheduleDrawerOpen();
    return currentPathForBta();
  }
  itemsAddedToCart.__btaDrawerHook = true;

  function wrapOnSuccess(existing) {
    const wrapped = function () {
      if (typeof existing === 'function' && !existing.__btaDrawerHook) {
        existing.apply(this, arguments);
      }
      // Do not throw: BookThatApp treats that as a failed add and leaves
      // queueLoading=true, which blocks the next date/book attempt.
      scheduleDrawerOpen();
    };
    wrapped.__btaDrawerHook = true;
    return wrapped;
  }

  function lockOnSuccess(settings) {
    if (!settings || settings.__btaDrawerLocked) return;

    let current = wrapOnSuccess(settings.onSuccess);
    try {
      Object.defineProperty(settings, 'onSuccess', {
        configurable: true,
        enumerable: true,
        get() {
          return current;
        },
        set(fn) {
          if (fn && fn.__btaDrawerHook) {
            current = fn;
            return;
          }
          current = wrapOnSuccess(fn);
        },
      });
      Object.defineProperty(settings, '__btaDrawerLocked', {
        configurable: true,
        enumerable: false,
        value: true,
      });
    } catch (error) {
      settings.onSuccess = current;
    }
  }

  function patchBtaConfig() {
    window.Bta = window.Bta || {};
    window.Bta.callbacks = window.Bta.callbacks || {};
    if (window.Bta.callbacks.itemsAddedToCart !== itemsAddedToCart) {
      window.Bta.callbacks.itemsAddedToCart = itemsAddedToCart;
    }

    if (!window.BtaConfig) return false;
    window.BtaConfig.settings = window.BtaConfig.settings || {};
    lockOnSuccess(window.BtaConfig.settings);

    const widgetSettings = window.BtaConfig.settings.widgetSettings || {};
    if (widgetSettings.redirect === 'cart' || widgetSettings.redirect === '/cart') {
      widgetSettings.redirect = '';
      window.BtaConfig.settings.widgetSettings = widgetSettings;
    }

    hookJQueryAjax();
    return true;
  }

  function looksLikeBtaAdd(body) {
    if (!body) return false;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      for (const key of body.keys()) {
        if (key.indexOf('_addons_uuid') !== -1) return true;
        if (/properties\[(.*start.*|.*finish.*|booking[^]]*)\]/i.test(key)) return true;
      }
      return false;
    }

    if (typeof body === 'string') {
      return body.indexOf('_addons_uuid') !== -1 || /properties(%5B|\[).*start/i.test(body);
    }

    return false;
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function requestBody(input, init) {
    if (init && init.body) return init.body;
    if (input && input.body) return input.body;
    return null;
  }

  const originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function (input, init) {
      const url = requestUrl(input);
      const body = requestBody(input, init);
      const isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      const isBtaAdd = isAdd && looksLikeBtaAdd(body);
      const isMutation = isPostCartMutation(url, requestMethod(input, init));
      if (isBtaAdd) {
        armSuppress();
        armReloadSuppress();
      }
      if (isMutation) armReloadSuppress();

      return originalFetch.apply(this, arguments).then((response) => {
        if (isBtaAdd && response && response.ok) scheduleDrawerOpen();
        if (isMutation && response && response.ok) onCartMutated();
        return response;
      });
    };
  }

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__btaRequestUrl = url;
    this.__btaRequestMethod = method;
    return originalXhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    const url = String(this.__btaRequestUrl || '');
    if (/\/cart\/add(\.js)?(\?|$)/.test(url) && looksLikeBtaAdd(body)) {
      armSuppress();
      armReloadSuppress();
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleDrawerOpen();
      });
    }
    if (isPostCartMutation(url, this.__btaRequestMethod)) {
      armReloadSuppress();
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) onCartMutated();
      });
    }
    return originalXhrSend.apply(this, arguments);
  };

  function hookJQueryAjax() {
    if (!window.jQuery || !window.jQuery.ajax || window.jQuery.ajax.__btaDrawerHook) return;
    const originalAjax = window.jQuery.ajax;
    window.jQuery.ajax = function (url, settings) {
      const opts = typeof url === 'object' ? url : settings;
      const requestUrlValue = typeof url === 'string' ? url : opts && opts.url;
      const method = (opts && opts.type) || (opts && opts.method) || (typeof url === 'object' && url.type) || 'GET';
      const result = originalAjax.apply(this, arguments);
      if (isPostCartMutation(requestUrlValue, method) && result && typeof result.always === 'function') {
        result.always(onCartMutated);
      } else if (isPostCartMutation(requestUrlValue, method)) {
        onCartMutated();
      }
      return result;
    };
    window.jQuery.ajax.__btaDrawerHook = true;
  }

  function interceptReloadOrSamePage(url) {
    if (suppressReload && getCartDrawer() && (url == null || isSamePage(url))) {
      refreshCartDrawer({ open: suppressCartRedirect });
      return true;
    }
    return false;
  }

  function interceptCartNavigation(url) {
    if (interceptReloadOrSamePage(url)) return true;
    if (!suppressCartRedirect || !isCartUrl(url)) return false;
    suppressCartRedirect = false;
    openThemeCartDrawer();
    return true;
  }

  try {
    const originalReload = Location.prototype.reload;
    Location.prototype.reload = function () {
      if (interceptReloadOrSamePage(window.location.href)) return;
      return originalReload.apply(this, arguments);
    };
  } catch (error) {
    // reload is not always patchable.
  }

  try {
    const assign = Location.prototype.assign;
    const replace = Location.prototype.replace;
    if (typeof assign === 'function') {
      Location.prototype.assign = function (url) {
        if (interceptCartNavigation(url)) return;
        return assign.call(this, url);
      };
    }
    if (typeof replace === 'function') {
      Location.prototype.replace = function (url) {
        if (interceptCartNavigation(url)) return;
        return replace.call(this, url);
      };
    }
  } catch (error) {
    // Location methods are not always patchable.
  }

  try {
    const hrefDescriptor = Object.getOwnPropertyDescriptor(Location.prototype, 'href');
    if (hrefDescriptor && hrefDescriptor.configurable && hrefDescriptor.set) {
      Object.defineProperty(Location.prototype, 'href', {
        configurable: true,
        enumerable: hrefDescriptor.enumerable,
        get: hrefDescriptor.get,
        set(value) {
          if (interceptCartNavigation(value)) return;
          hrefDescriptor.set.call(this, value);
        },
      });
    }
  } catch (error) {
    // href is not configurable in most browsers.
  }

  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', (event) => {
      try {
        if (suppressReload && event.navigationType === 'reload' && getCartDrawer()) {
          if (typeof event.preventDefault === 'function') event.preventDefault();
          refreshCartDrawer();
          return;
        }
        if (suppressReload && isSamePage(event.destination.url) && getCartDrawer()) {
          if (typeof event.preventDefault === 'function') event.preventDefault();
          refreshCartDrawer();
          return;
        }
        if (!suppressCartRedirect) return;
        if (!isCartUrl(event.destination.url)) return;
        const source = event.sourceElement;
        if (source && source.closest && source.closest('a[href*="/cart"]')) return;
        if (typeof event.preventDefault === 'function') event.preventDefault();
        suppressCartRedirect = false;
        openThemeCartDrawer();
      } catch (error) {
        // Ignore navigation intercept failures.
      }
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target && event.target.closest && event.target.closest('[class*="bta-"], #bta-product-widget, .bta-product-widget');
      if (target) armSuppress();

      const changeLink =
        event.target &&
        event.target.closest &&
        event.target.closest('a[href*="/cart/change"]');
      if (!changeLink) return;
      const drawer = getCartDrawer();
      if (!drawer || !drawer.contains(changeLink)) return;
      if (/\.js(\?|$)/.test(changeLink.getAttribute('href') || '')) return;

      event.preventDefault();
      event.stopPropagation();
      armReloadSuppress();

      const removeButton = changeLink.closest('cart-remove-button');
      const cartItems =
        (removeButton && (removeButton.closest('cart-drawer-items') || removeButton.closest('cart-items'))) ||
        drawer.querySelector('cart-drawer-items');
      if (removeButton && cartItems && typeof cartItems.updateQuantity === 'function') {
        cartItems.updateQuantity(removeButton.dataset.index, 0);
        return;
      }

      fetch(changeLink.href.replace(/\/cart\/change(\?)/, '/cart/change.js$1'), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
        .then(() => onCartMutated())
        .catch(() => onCartMutated());
    },
    true
  );

  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.message === BTA_ABORT) event.preventDefault();
  });

  const originalOnError = window.onerror;
  window.onerror = function (message) {
    if (typeof message === 'string' && message.indexOf(BTA_ABORT) !== -1) return true;
    if (typeof originalOnError === 'function') return originalOnError.apply(this, arguments);
    return false;
  };

  function bounceOffCartPage() {
    if (!isCartPath(window.location.pathname)) {
      try {
        const stamped = Number(sessionStorage.getItem(OPEN_KEY) || 0);
        sessionStorage.removeItem(OPEN_KEY);
        if (!stamped || Date.now() - stamped > 15000) return;
        const openWhenReady = function () {
          if (getCartDrawer()) {
            openThemeCartDrawer();
          } else {
            setTimeout(openWhenReady, 50);
          }
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', openWhenReady, { once: true });
        } else {
          openWhenReady();
        }
      } catch (error) {
        // Ignore sessionStorage failures.
      }
      return;
    }

    try {
      const stamped = Number(sessionStorage.getItem(INTENT_KEY) || 0);
      if (stamped && Date.now() - stamped < 15000) {
        sessionStorage.removeItem(INTENT_KEY);
        sessionStorage.setItem(OPEN_KEY, String(Date.now()));
        if (window.history.length > 1) {
          window.history.back();
        } else if (document.referrer) {
          window.location.replace(document.referrer);
        } else {
          const preview = window.location.search || '';
          window.location.replace(`/${preview}`);
        }
      }
    } catch (error) {
      // Ignore sessionStorage failures.
    }
  }

  patchBtaConfig();
  setInterval(patchBtaConfig, 250);
  bounceOffCartPage();

  document.addEventListener(
    'click',
    (event) => {
      if (event.target && event.target.closest && event.target.closest('.drawer__close, #CartDrawer-Overlay, cart-drawer .drawer__overlay')) {
        setTimeout(resetBtaWidget, 200);
      }
    },
    true
  );

  function watchDrawerClose() {
    const drawerEl = getCartDrawer();
    if (!drawerEl || drawerEl.__btaCloseWatched || typeof MutationObserver !== 'function') return;
    drawerEl.__btaCloseWatched = true;
    new MutationObserver(() => {
      if (!drawerEl.classList.contains('active')) resetBtaWidget();
    }).observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawerClose, { once: true });
  } else {
    watchDrawerClose();
  }
})();
